// AgentTokensClient covers sandbox-scoped agent access tokens — the bearers
// an EXTERNAL agent (Claude Code, Cursor, Codex, …) presents to the MCP
// server (/mcp) to drive this sandbox's cloud agent. Each sandbox can have
// multiple tokens (one per client, rotation).
//
// The plaintext bearer is returned EXACTLY ONCE — at create time. The
// frontend must surface it ("copy now") before navigating away; the server
// stores only a sha256 hash, so a lost plaintext is unrecoverable (delete +
// recreate).
import type { BaseClient } from '../../http/BaseClient.js';
import {
  AgentToken,
  CreateAgentTokenRequest,
  CreateAgentTokenResponse,
  SimpleKeysResponse,
  WireAgentToken,
  normalizeAgentToken,
} from './types.js';

export class AgentTokensClient {
  constructor(private readonly http: BaseClient) {}

  async list(sandboxId: string): Promise<AgentToken[]> {
    const res = await this.http.get<{ success: boolean; tokens?: WireAgentToken[]; data?: WireAgentToken[] }>(
      `/sandbox/${sandboxId}/agent-token`,
    );
    const list = res.tokens ?? res.data ?? [];
    return list.map(normalizeAgentToken);
  }

  /**
   * create returns both the persisted token row and the plaintext bearer
   * (`secret`). The bearer is shown only here; surface it to the user
   * immediately. If they navigate away without copying, they must delete the
   * token and create a new one.
   */
  async create(sandboxId: string, body: CreateAgentTokenRequest): Promise<CreateAgentTokenResponse> {
    const res = await this.http.post<{
      success: boolean;
      token?: WireAgentToken;
      secret?: string;
    }>(`/sandbox/${sandboxId}/agent-token`, { body });
    if (!res.token || !res.secret) {
      throw new Error('createAgentToken response missing token or secret.');
    }
    return {
      token: normalizeAgentToken(res.token),
      secret: res.secret,
    };
  }

  delete(sandboxId: string, tokenId: string): Promise<SimpleKeysResponse> {
    return this.http.delete<SimpleKeysResponse>(`/sandbox/${sandboxId}/agent-token/${tokenId}`);
  }
}
